<template>
  <div>
    <!-- Header -->
    <slot name="header" :downloadPdf="downloadPdf" :isDownloadingPdf="isDownloadingPdf" :displayRegion="displayRegion">
      <div class="mb-6">
        <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
          <div>
            <div class="text-xs font-extrabold text-purple-600 uppercase tracking-widest">
              {{ t("brief_title") }}
            </div>
            <div v-if="selectableRegions.length > 0" class="mt-2">
              <Selector
                id="region-selector"
                name="region"
                :label="t('select_region')"
                :modelValue="selectedRegion"
                :options="regionOptions"
                @update:modelValue="handleRegionChange"
                class="w-full sm:w-72"
              />
            </div>
            <div v-else class="text-2xl font-extrabold text-gray-900 tracking-tight mt-1">
              {{ t("region_title", { name: currentUserRegion }) }}
            </div>
            <div class="text-sm text-gray-400 mt-1">{{ $d(new Date(), "fullDate") }}</div>
          </div>
          <SecondaryButton
            v-if="displayRegion"
            :loading="isDownloadingPdf"
            class="w-full sm:w-auto sm:shrink-0"
            @click="downloadPdf"
          >
            <DownloadIcon v-if="!isDownloadingPdf" class="h-4 w-4 mr-2" />
            {{ t("download_brief") }}
          </SecondaryButton>
        </div>
      </div>
    </slot>

    <div v-if="roadmapPerformance">
      <!-- KPI Cards -->
      <div class="grid grid-cols-3 gap-2 mb-4">
        <KpiCard :value="yesterdayPerformance?.salesCurrent ?? 0" :label="t('sales_yesterday')" />
        <KpiCard
          :value="monthlyPerformance?.salesCurrent ?? 0"
          :label="t('sales_this_month')"
          :variation="monthlyPerformance?.variation"
        />
        <KpiCard :label="t('active_stores')">
          <template #value>
            <span class="text-red-600">{{ activeStores?.active ?? 0 }}</span>
            <span class="text-sm font-bold">/{{ activeStores?.total ?? 0 }}</span>
            <span class="text-xs font-normal text-gray-400 italic">({{ activeStoresPercent }}%)</span>
          </template>
        </KpiCard>
      </div>

      <!-- Objective -->
      <ObjectiveBlock :title="t('objective_title')" :monthlyPerformance="monthlyPerformance" />

      <!-- Regional blocks -->
      <div class="space-y-4">
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <RegionRankingBlock
            v-if="regionRanking.regions && regionRanking.regions.length > 0"
            :regionRanking="regionRanking.regions"
            :totalRegions="regionRanking.total"
          />
          <StoresToRelaunchBlock v-if="storesToRelaunch.length > 0" :storesToRelaunch="storesToRelaunch" />
        </div>
        <div class="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <TopStoresBlock v-if="storeRanking" :storeRanking="storeRanking || []" :title="t('top_stores_title')" />
          <TopSellersBlock v-if="sellerRanking" :sellerRanking="sellerRanking || []" :title="t('top_sellers_title')" />
        </div>

        <!-- Based on the user's own stores, so not shown to an organization admin browsing a region -->
        <TrainingBlock v-if="isRegionalManager" />

        <ComplianceBlock
          v-if="hasSeatsOrFurniture && compliance && compliance.regionRate != null"
          multiStore
          :compliance="compliance"
        />
      </div>
    </div>
    <Loading v-else-if="selectedRegion || selectableRegions.length === 0" />
  </div>
</template>

<script>
import KpiCard from "@/components/roadmap/KpiCard.vue";
import ObjectiveBlock from "@/components/roadmap/ObjectiveBlock.vue";
import RegionRankingBlock from "@/components/roadmap/RegionRankingBlock.vue";
import StoresToRelaunchBlock from "@/components/roadmap/StoresToRelaunchBlock.vue";
import TopStoresBlock from "@/components/roadmap/TopStoresBlock.vue";
import TopSellersBlock from "@/components/roadmap/TopSellersBlock.vue";
import ComplianceBlock from "@/components/roadmap/ComplianceBlock.vue";
import TrainingBlock from "@/components/roadmap/TrainingBlock.vue";
import AnalyticsService from "@/api/services/analytics";
import { Loading, Selector, SecondaryButton, toCamelCase, download } from "@estaly/ui";
import { DownloadIcon } from "@heroicons/vue/outline";
import { mapGetters } from "vuex";
import { useI18n } from "vue-i18n";

export default {
  components: {
    KpiCard,
    ObjectiveBlock,
    RegionRankingBlock,
    StoresToRelaunchBlock,
    TopStoresBlock,
    TopSellersBlock,
    ComplianceBlock,
    TrainingBlock,
    Loading,
    Selector,
    SecondaryButton,
    DownloadIcon,
  },
  props: {
    selectableRegions: {
      type: Array,
      default: () => [],
    },
  },
  setup() {
    const { t } = useI18n();
    return { t };
  },
  data: () => ({
    roadmapPerformance: null,
    yesterdayPerformance: null,
    monthlyPerformance: null,
    sellerRanking: null,
    storeRanking: null,
    regionRanking: {},
    activeStores: null,
    storesToRelaunch: [],
    compliance: {},
    selectedRegion: null,
    isDownloadingPdf: false,
    loadId: 0,
  }),
  computed: {
    ...mapGetters("auth", ["currentUserRegion", "isRegionalManager"]),
    ...mapGetters("businessVerticals", ["hasSeatsOrFurniture"]),
    dataSource() {
      return this.hasSeatsOrFurniture ? "gross_sales" : "plan_sales";
    },
    displayRegion() {
      return this.selectableRegions.length > 0 ? this.selectedRegion : this.currentUserRegion;
    },
    regionOptions() {
      return this.selectableRegions.map((region) => ({
        value: region,
        label: region,
      }));
    },
    activeStoresPercent() {
      const total = this.activeStores?.total;
      const active = this.activeStores?.active;
      if (!total || total === 0) return 0;
      return Math.round((active / total) * 100);
    },
  },
  mounted() {
    if (this.selectableRegions.length === 0) {
      this.fetchData();
    }
  },
  methods: {
    handleRegionChange(region) {
      this.selectedRegion = region;
      this.roadmapPerformance = null;
      this.fetchData();
    },
    async fetchData() {
      const currentLoadId = ++this.loadId;
      const region = this.selectableRegions.length > 0 ? this.selectedRegion : null;
      const response = await AnalyticsService.getRegionalRoadmapPerformance(this.dataSource, region);
      if (currentLoadId !== this.loadId) return;
      if (!response?.success) return;
      const data = toCamelCase(response.data);
      this.roadmapPerformance = data;
      this.yesterdayPerformance = data.yesterdayPerformance;
      this.monthlyPerformance = data.monthlyPerformance;
      this.sellerRanking = data.sellerRanking;
      this.storeRanking = data.storeRanking;
      this.regionRanking = data.regionRanking || {};
      this.activeStores = data.activeStores || null;
      this.storesToRelaunch = data.storesToRelaunch || [];
      this.compliance = data.compliance || {};
    },
    async downloadPdf() {
      this.isDownloadingPdf = true;
      try {
        const region = this.displayRegion;
        const response = await AnalyticsService.downloadRegionalRoadmapPdf(this.dataSource, region);
        download(response.data, `brief-regional-${region}.pdf`);
      } finally {
        this.isDownloadingPdf = false;
      }
    },
  },
};
</script>

<i18n src="./RegionalRoadmapOverview.i18n.json"></i18n>
